<template>
  <v-dialog v-model="dialog" width="1000" :persistent="true">
    <template v-slot:activator="{ on }">
      <v-btn color="primary" class="ml-0 mr-0" @click="open" :disabled="disabled">
        {{ 'app.prescriptions.add_prescription' | trans }}
      </v-btn>
    </template>

    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>
          {{
            (!!prescriptionId ? 'app.prescriptions.edit_prescription' : 'app.prescriptions.add_prescription') | trans
          }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <permission-tooltip
                    :tooltip="{top: true, bottom: false, attach: false}"
                >
                  <v-text-field
                      :label="'app.name' | trans"
                      v-model="prescription.name"
                      :rules="rules.name"
                      full
                  ></v-text-field>
                </permission-tooltip>
              </v-flex>

              <v-flex xs12>
                <editor v-model="prescription.description"
                        :tinymce-script-src="'/js/tinymce/tinymce.min.js'"
                        :init=editorConfig>
                </editor>
                <v-messages class="ml-2 mt-2" v-if="descriptionValidationErrors.length" :value="descriptionValidationErrors" :color="`error`"></v-messages>
              </v-flex>
              <v-flex xs6 d-flex v-if="hasPermission('prescription_system_create')">
                <v-checkbox
                    v-model="prescription.system"
                    :label="trans('app.system')"
                    color="primary"
                ></v-checkbox>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
        <v-btn color="primary" @click="save" :disabled="!valid || descriptionValidationErrors.length > 0" :loading="loading">
            <span v-if="prescriptionId && prescription.system && !hasPermission('prescription_system_edit') || prescription.shared">{{
                'app.prescriptions.save_as_own' | trans
              }}</span>
          <span v-else>{{ 'app.save' | trans }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {trans} from "../../../app";
import {mapGetters} from 'vuex';
import RulesBuilder from "../../../utils/rulesBuilder";

export default {
  data: function () {
    return {
      dialog: false,
      valid: false,
      loading: false,
      prescriptionId: null,
      prescription: {
        name: '',
        description: '',
        system: false,
        shared: false,
      },
      rulesBuilder: new RulesBuilder(),
      editorConfig: {
        height: 500,
        language_url: '/js/tinymce/langs/pl.js',
        language: 'pl',
        plugins: [
            'lists',
        ],
        toolbar: 'undo redo | blocks | fontsize | bold italic underline forecolor backcolor | bullist numlist alignleft aligncenter ' +
            'alignright alignjustify outdent indent | copy cut paste | removeformat',
        promotion: false,
        menubar: false,
        statusbar: false,
        block_formats: 'Akapit=p; Nagłówek 1=h1; Nagłówek 2=h2; Nagłówek 3=h3; Nagłówek 4=h4; Nagłówek 5=h5; Nagłówek 6=h6'
      },
      descriptionValidationErrors: [],
    }
  },
  props: {
    currentPrescription: Object,
    disabled: Boolean
  },
  watch: {
    currentPrescription: async function (prescription) {
      if (prescription === null) {
        this.prescriptionId = null;
        this.prescription.name = '';
        this.prescription.description = '';
        this.prescription.system = false;
        this.prescription.shared = false;
      } else {
        this.prescriptionId = prescription.id;
        this.prescription.name = prescription.name;
        const { data } = await axios.get('/v2/prescriptions/' + prescription.id);
        this.prescription.description = data.data.description;
        this.prescription.system = prescription.system;
        this.prescription.shared = prescription.shared;
      }
      this.dialog = true;
    },
    'prescription.description': function (newValue) {
      this.validateDescription(newValue);
    }
  },
  computed: {
    ...mapGetters(['hasPermission']),
    rules: function () {
      return {
        name: [
          ...this.rulesBuilder
              .addRequiredRule(trans('app.name'))
              .addMinLengthRule(trans('app.name'), 1)
              .addMaxLengthRule(trans('app.name'), 50)
              .getRules()
        ],
      };
    },
  },
  methods: {
    open: function () {
      this.$refs.form.reset();
      this.prescription.description = '';
      this.prescriptionId = null;
      this.dialog = true;
    },
    close: function () {
      this.dialog = false;
      this.$refs.form.reset();
      this.descriptionValidationErrors = [];
    },
    save: async function () {
      const isDescriptionValid = this.validateDescription(this.prescription.description);
      const isFormValid = this.$refs.form.validate();
      if (isFormValid === false || isDescriptionValid === false) {
        return;
      }

      this.loading = true;
      if (this.prescriptionId === null) {
        if (typeof this.prescription.system === 'undefined') {
          this.prescription.system = false;
        }

        try {
          await axios.post('/v2/prescriptions', this.prescription);
          this.$toast.success(trans('app.actions.added', {name: trans('app.prescription_accusative')}));
        } catch (e) {
          this.$toast.error(trans('app.save_failed'));
          this.loading = false;
          return;
        }
      } else {
        const prescriptionId = this.prescriptionId;

        try {
          if ((this.prescription.system && !this.hasPermission('prescription_system_edit') || this.prescription.shared)) {
            this.prescription.system = false
            await axios.post('/v2/prescriptions', this.prescription);
            this.$toast.success(trans('app.actions.added', {name: trans('app.prescription_accusative')}));
          } else {
            await axios.put(`/v2/prescriptions/${prescriptionId}`, this.prescription);
            this.$toast.success(trans('app.actions.edited', {name: trans('app.prescription_accusative')}));
          }

        } catch (e) {
          this.$toast.error( trans('app.save_failed'));
          this.loading = false;
          return;
        }
      }
      this.$emit('prescription_stored');
      this.close();
      this.loading = false;
    },
    validateDescription(newValue) {
      const maxLength = 30000;
      const minLength = 1;
      if (newValue.length > maxLength) {
        this.descriptionValidationErrors = [trans('app.prescriptions.description_too_long')];
      } else if (newValue.length < minLength) {
        this.descriptionValidationErrors = [trans('app.prescriptions.description_too_short')];
      } else {
        this.descriptionValidationErrors = [];
      }
      return this.descriptionValidationErrors.length === 0;
    }
  }
}
</script>
